/* eslint-disable no-useless-escape */
import * as React from 'react';
import StandardModal from '../../../common/modals/StandardModal';
import './RuleModal.css';
import TextAreaField from '../../../common/form-controls/TextareaField';
import { ToggleField, Button, Grid, Icon } from '../../../common';
import TextInputField from '../../../common/form-controls/TextInputField';
import { AbstractRulesForm, IRuleStateForm, RulesFormProps } from './AbstractAWSRulesForm';
import { RuleAction } from '../../../../models/enums/POR/RuleAction';
import SelectField from '../../../common/form-controls/SelectField';
import { RuleOperation } from '../../../../models/enums/POR/RuleOperation';
import { PortOpeningRequestsDirection } from '../../../../models/enums/POR/PortOpeningRequestsDirection';
import { AWSRuleRegion } from '../../../../models/enums/POR/AWSRuleRegion';
import { TrafficDirection } from '../../../../models/enums/POR/TrafficDirection';
import { runInThisContext } from 'vm';
import ContainerLayout from '@emorg-prd/standard-react/layouts/ContainerLayout/ContainerLayout';

interface RuleModalAccToDMZProps extends RulesFormProps {

}

interface AccToDMZRuleStateForm extends IRuleStateForm {
    sourceURLs?: string;
}

interface RuleModalAccToDMZState {
    stateForm: AccToDMZRuleStateForm;
    availableProtocols: string[];
}

const MAX_PROTOCOLS = 4;

export default class RuleModalAccToDMZ extends AbstractRulesForm<
    RuleModalAccToDMZProps,
    RuleModalAccToDMZState
> {

    constructor(props: RuleModalAccToDMZProps) {
        super(props);
        this.state = {
            stateForm: this.props.isFromHistory ? this.getRuleCopy() : this.getDefaultForm(),
            availableProtocols: ['TCP', 'UDP', 'ICMP echo-request', 'ICMP echo-reply'],
        };

        this.initFormModel();
    }

    initFormModel() {
        super.initFormModel();

        this.addIpsRuleValidation()

        this.formModel.addField('sourceURLs', {
            getValue: () => this.state.stateForm.sourceURLs,
            validation: {
                required: () => !(this.state.stateForm.sourceIPs?.trim().length > 0),
                rules: [
                    {
                        assert: () => !this.hasEmptyValues(this.state.stateForm.sourceURLs),
                        message: 'Empty values are not allowed.'
                    },
                    {
                        assert: () => !(this.state.stateForm.sourceURLs?.includes('https://')
                            || this.state.stateForm.sourceURLs?.includes('http://')),
                        message: 'URL cannot have "http" or "https"'
                    },
                    this.banHashCharacterValidation(),
                    {
                        assert: (value) => {
                            const urls = this.parseMultipleValues(value)?.split(';');
                            return urls?.every(url => this.isURLValid(url));
                        },
                        message: (value) => {
                            const invalidAddress = this.parseMultipleValues(value)?.split(';').find(url => !this.isURLValid(url));
                            return `${invalidAddress} is an invalid URL.`;
                        }
                    }
                ]
            }
        })

        this.formModel.fields.sourceIPs.validation.required = () => !(this.state.stateForm.sourceURLs?.trim().length > 0);

        this.state.stateForm.sourceURLs = "";
    }

    componentDidUpdate(prevProps: any): void {
        super.componentDidUpdate(prevProps)

        if (prevProps.porDirection.name != this.props.porDirection.name) {
            this.addIpsRuleValidation();
        }
    }

    addIpsRuleValidation() {
        this.refreshIpsRuleValidation();

        if (this.props.isReadOnly) {
            this.removeIpsRuleValidation()
            if (this.formModel.fields.sourceURLs?.validation) {
                this.formModel.fields.sourceURLs.validation.required = () => false
                this.formModel.fields.sourceURLs.validation.rules = []
            }
            return;
        }

        this.formModel.fields.destinationURLs.validation.rules.push(
            this.banHashCharacterValidation()
        );

        if (this.props.porDirection.name == PortOpeningRequestsDirection.InternetToDMZNorth.name) {
            this.destinationIpsValidation()
        } else {
            this.sourceIpsValidation()
        }

        this.formModel.fields.sourceIPs.validation.required = () => !(this.state.stateForm.sourceURLs?.trim().length > 0);

        this.formModel.fields.destinationIPs.validation.rules.push(this.numberOfDestinationItemsValidation());
    }

    // eslint-disable-next-line class-methods-use-this
    getDefaultFormState() {
        return {
            isTemporary: false,
            expirationDate: null,
            operation: RuleOperation.Add,
            name: '',
            destinationURLs: '',
            destinationPorts: [{ protocol: '', destinationPorts: '' }],
        };
    }

    getRegionOptions(): AWSRuleRegion[] {
        var accToInternetRegions = new AWSRuleRegion().awsListByDirection(PortOpeningRequestsDirection.AccountToInternet, this.props.virtualPrivateClouds.primaryAccountVirtualPrivateClouds);
        return accToInternetRegions;
    }

    addProtocol = () => {
        if (this.state.stateForm.destinationPorts.length < MAX_PROTOCOLS) {
            const updatedProtocols = [...this.state.stateForm.destinationPorts];
            updatedProtocols.push({ protocol: '', destinationPorts: '' });
            this.formModel.fields.destinationPorts.validation.rules.push(
                {
                    assert: () => this.state.stateForm?.destinationPorts[this.state.stateForm?.destinationPorts.length - 1]?.protocol !== '',
                    message: 'Empty values are not allowed.'
                },
                {
                    assert: () => !this.hasEmptyValues(this.state.stateForm?.destinationPorts[this.state.stateForm?.destinationPorts.length - 1]?.destinationPorts),
                    message: 'Empty values are not allowed.'
                },
                {
                    assert: () => {
                        const ports = this.parseMultipleValues(this.state.stateForm?.destinationPorts[this.state.stateForm?.destinationPorts.length - 1]?.destinationPorts)?.split(';');
                        return ports.every(port => this.isPortInputValid(port, 0));
                    },
                    message: () => {
                        const invalidPort = this.parseMultipleValues(this.state.stateForm?.destinationPorts[this.state.stateForm?.destinationPorts.length - 1]?.destinationPorts).split(';').find(port => !this.isPortInputValid(port, 0));
                        return `${invalidPort} is an invalid port input.`;
                    }
                }
            );
            this.handleStateFormChange('destinationPorts', updatedProtocols);
        }
    };

    handleProtocolChange = (index: number, field: string, value: string) => {
        const updatedProtocols = [...this.state.stateForm.destinationPorts];
        updatedProtocols[index][field] = value;
        this.handleStateFormChange('destinationPorts', updatedProtocols);
    };

    removeProtocol = (index: number) => {
        const updatedProtocols = this.state.stateForm.destinationPorts.filter((_, i) => i !== index);
        this.handleStateFormChange('destinationPorts', updatedProtocols);
        this.formModel.fields.destinationPorts.validation.rules = this.formModel.fields.destinationPorts.validation.rules?.filter((_, i) => i !== index);
    };

    // Rendering protocols dynamically
    renderProtocols() {
        return this.state.stateForm.destinationPorts?.map((protocol, index) => {

            const selectedProtocols = this.state.stateForm.destinationPorts
                .slice(0, index)
                .map(p => p.protocol);
            const availableOptions = this.state.availableProtocols.filter(
                protocol => !selectedProtocols.includes(protocol)
            );

            return (
                <Grid variant="3up" key={index}>
                    <Grid.Item>
                        <TextAreaField
                            id={`destinationPorts-${index}`}
                            note='Multiple values are allowed, separated by semicolons. Port ranges are accepted.'
                            value={this.state.stateForm.destinationPorts[index]?.destinationPorts}
                            validation={this.formModel.fields.destinationPorts.validation}
                            label={`Destination Port(s) for Protocol ${index + 1}`}
                            onChange={(e) => this.handleProtocolChange(index, 'destinationPorts', e.target.value)}
                            disabled={!this.formCanBeEdited()}
                        />
                    </Grid.Item>
                    <Grid.Item style={{ alignSelf: 'baseline' }}>
                        <SelectField
                            label={`Protocol ${index + 1}`}
                            id={`protocol-${index}`}
                            value={this.state.stateForm.destinationPorts[index]?.protocol}
                            validation={this.formModel.fields.destinationPorts.validation}
                            options={availableOptions}
                            onChange={(e) => this.handleProtocolChange(index, 'protocol', e.target.value)}
                            disabled={!this.formCanBeEdited()}
                        />
                    </Grid.Item>
                    <Grid.Item style={{ width: '10%' }}>
                        {index != 0 &&
                            <Button
                                style={{ zIndex: 0 }}
                                variant="primary"
                                size="small"
                                onClick={() => this.removeProtocol(index)}
                                disabled={!this.formCanBeEdited()}
                            >
                                <Icon icon="trashcan" size="small" />
                            </Button>
                        }
                    </Grid.Item>
                </Grid>
            )
        });
    }

    wasEditted() {
        if (this.props.editingRule) {
            return (
                this.props.editingRule.description != this.stateFormHandler().description.value
                || this.props.editingRule.action.value != this.stateFormHandler().action.value
                || this.props.editingRule.sourceIPs != this.stateFormHandler().sourceIPs.value
                || this.props.editingRule.destinationIPs != this.stateFormHandler().destinationIPs.value
                || this.props.editingRule.destinationURLs != this.state.stateForm.destinationURLs
                || this.props.editingRule.destinationPorts != this.state.stateForm.destinationPorts
                || this.props.editingRule.sendDND != this.state.stateForm.sendDND
                || (this.props.editingRule.operation.value == RuleOperation.Add.value && (this.props.editingRule.name != this.state.stateForm.name))
            )
        }
        return true
    }

    hasSourceURL = () => {
        return this.props.porDirection != undefined &&
            (this.props.porDirection.name == PortOpeningRequestsDirection.DMZTrustedToL4South.name ||
                this.props.porDirection.name == PortOpeningRequestsDirection.DMZToInternetSouthObi.name)
    }

    render() {
        return (
            <StandardModal
                optionalClass="ruleModalAWS"
                show={this.props.visible}
                title={this.props.isReadOnly ? 'View Rule' : !this.isModalForNewRule() ? 'Edit Rule' : 'New Rule'}
                onDismiss={this.props.onClose as () => any}
                footer={
                    !this.props.isReadOnly &&
                    <Grid variant="2up">
                        <Grid.Item>
                            <Button
                                variant="primary"
                                disabled={(!this.formModel.isValid() || !this.wasEditted()) && this.state.stateForm.operation.name !== RuleOperation.Remove.name}
                                onClick={this.handleRequest}
                            >
                                {this.props.editingRule ? 'Confirm' : 'Create'}
                            </Button>
                        </Grid.Item>
                        <Grid.Item>
                            <Button
                                variant="secondary"
                                onClick={this.handleCancel}
                            >
                                Cancel
                            </Button>
                        </Grid.Item>
                    </Grid>
                }
            >
                {!this.isModalForNewRule() &&
                    <ToggleField
                        {...this.stateFormHandler().operation}
                        label="Operation"
                        id='operation-toggle'
                        disabled={this.props.isReadOnly}
                        value={this.state.stateForm.operation?.value}
                        options={this.props.isReadOnly ?
                            [
                                {
                                    value: new RuleOperation().fromValue(this.stateFormHandler().operation.value).value,
                                    text: new RuleOperation().fromValue(this.stateFormHandler().operation.value).name,
                                }
                            ] :
                            [
                                { value: RuleOperation.Modify.value, text: RuleOperation.Modify.name },
                                { value: RuleOperation.Remove.value, text: RuleOperation.Remove.name }
                            ]
                        }
                    />
                }
                <Grid variant="2up">
                    <Grid.Item>
                        <TextInputField
                            label="Rule Name"
                            value={this.state.stateForm.name}
                            validation={this.formModel.fields.name.validation}
                            onChange={
                                (event) => {
                                    this.handleStateFormChange('name', event.target.value);
                                }
                            }
                            disabled={this.state.stateForm.operation?.name !== RuleOperation.Add.name || !this.formCanBeEdited()}
                        />
                    </Grid.Item>
                    <Grid.Item>
                        <SelectField
                            {...this.stateFormHandler().region}
                            options={this.getRegionOptions()}
                            disabled={this.state.stateForm.operation?.name !== RuleOperation.Add.name || !this.formCanBeEdited()}
                        />
                    </Grid.Item>
                </Grid>
                {this.state.stateForm.operation?.name !== RuleOperation.Remove.name &&
                    <>
                        <Grid variant="2up">
                            <Grid.Item>
                                <ToggleField
                                    id='action-toggle'
                                    {...this.stateFormHandler().action}
                                    options={(this.formCanBeEdited()) ? [
                                        { value: RuleAction.Allow.value, text: RuleAction.Allow.name },
                                        { value: RuleAction.Block.value, text: RuleAction.Block.name },
                                    ] : this.stateFormHandler().action?.value ?
                                        [{ value: RuleAction.Allow.value, text: RuleAction.Allow.name }]
                                        :
                                        [{ value: RuleAction.Block.value, text: RuleAction.Block.name }]
                                    }
                                    label="Action"
                                    disabled={!this.formCanBeEdited()}
                                />
                            </Grid.Item>
                        </Grid>
                        <Grid variant="2up">
                            <Grid.Item>
                                <TextAreaField
                                    {...this.stateFormHandler().sourceIPs}
                                    label="Source (IPs / IP Range / CIDR)"
                                    disabled={!this.formCanBeEdited()}
                                />
                            </Grid.Item>
                            <Grid.Item>
                                <TextAreaField
                                    {...this.stateFormHandler().destinationIPs}
                                    label="Destination (IPs / IP range / CIDR)"
                                    disabled={!this.formCanBeEdited() || (this.state.stateForm.destinationURLs?.trim().length > 0)}
                                />
                            </Grid.Item>
                        </Grid>
                        <Grid variant="2up">
                            {this.hasSourceURL() &&
                                <Grid.Item>
                                    <TextAreaField
                                        label="Source (URLs)"
                                        value={this.state.stateForm.sourceURLs}
                                        validation={this.formModel.fields.sourceURLs.validation}
                                        onChange={
                                            (event) => {
                                                this.handleStateFormChange('sourceURLs', event.target.value);
                                            }
                                        }
                                        note='Multiple values are allowed, separated by semicolons.'
                                        disabled={!this.formCanBeEdited()}
                                    />
                                </Grid.Item>
                            }
                            <Grid.Item>
                                <TextAreaField
                                    label="Destination (URLs)"
                                    value={this.state.stateForm.destinationURLs}
                                    validation={this.formModel.fields.destinationURLs.validation}
                                    onChange={
                                        (event) => {
                                            this.handleStateFormChange('destinationURLs', event.target.value);
                                        }
                                    }
                                    note='Multiple values are allowed, separated by semicolons.'
                                    disabled={!this.formCanBeEdited() || (this.state.stateForm.destinationIPs?.trim().length > 0)}
                                />
                            </Grid.Item>
                        </Grid>
                        <ContainerLayout optionalClass="ruleModal-Container" type="em-l-container">
                            {this.renderProtocols()}
                            <Grid>
                                <Grid.Item />
                                <Grid.Item>
                                    <Button
                                        variant="primary"
                                        size="small"
                                        style={{ width: '30%', alignSelf: 'flex-end' }}
                                        onClick={() => this.addProtocol()}
                                        disabled={!this.formCanBeEdited() || this.state.stateForm?.destinationPorts.length >= MAX_PROTOCOLS}
                                    >
                                        <>
                                            <Icon icon="plus-filled" size="small" />
                                            <span>Add Protocol</span>
                                        </>
                                    </Button>
                                </Grid.Item>
                            </Grid>
                        </ContainerLayout>
                        <header className="em-c-section__header" />
                        <Grid variant="2up">
                            <Grid.Item>
                                <TextAreaField
                                    {...this.stateFormHandler().description}
                                    label="Description"
                                    disabled={!this.formCanBeEdited()}
                                />
                            </Grid.Item>
                            <Grid.Item />
                        </Grid>
                    </>}
            </StandardModal>
        );
    }
}